/**
 * Eine Aufnahme in ein Format bringen, das jedes Gerät abspielt.
 *
 * **Der Server kann das nicht** — kein ffmpeg auf dem Paket, er legt ab, was
 * kommt, und liefert es genau so wieder aus. Also rechnet der Browser. Das
 * ist keine neue Idee in dieser Anwendung, sondern dieselbe wie bei den
 * Fotos (`images.ts`): die Umwandlung gehört dorthin, wo die Datei entsteht.
 *
 * Gewandelt wird nach **WAV**, und das ist eine Entscheidung mit einem Preis:
 * eine Minute wiegt rund 1,9 MB statt 0,5 MB in MP3 oder AAC. Dafür braucht
 * es hier keine fremde Bibliothek und kein WebAssembly — und WAV spielt
 * buchstäblich jedes Gerät ab, auch ein altes.
 *
 * **Das gilt für den Ton und ist keine Hausregel.** Hier stand einmal „keine
 * Lizenzfrage", als sei jede Abhängigkeit im Browser ausgeschlossen; seit dem
 * Verkleinern der Videos (`videowandeln.ts`) stimmt das nicht mehr. Der
 * Unterschied ist der Aufwand: ein WAV-Kopf sind vierundvierzig Bytes, ein
 * MP4 ist ein Baum aus Boxen. Wo es von Hand geht, geht es von Hand.
 *
 * **Es läuft nur im Rückfall.** Wo `MediaRecorder` MP4/AAC kann — Safari
 * immer, neueres Chrome meistens —, wird gar nichts umgerechnet. Betroffen
 * sind Firefox, ältere Chrome-Fassungen und manche Android-Geräte; deren
 * Aufnahmen blieben beim Empfänger auf dem iPhone sonst stumm.
 */

/**
 * 16 kHz, ein Kanal.
 *
 * Das ist Sprachbandbreite: verständlich und nicht dumpf. Darunter klingt es
 * nach Telefon, darüber wird die Datei größer, ohne dass eine Stimme davon
 * gewinnt. Ein Mikrofon liefert ohnehin meist mono.
 */
export const ABTASTRATE = 16_000

/**
 * Aus einer Aufnahme eine WAV-Datei machen.
 *
 * Zwei Schritte, und beide macht der Browser selbst: **dekodieren** kann er
 * jedes Format, das er auch aufnimmt, und **herunterrechnen** erledigt der
 * `OfflineAudioContext` mit. Genau das sind die zwei Stellen, an denen
 * handgeschriebener Code scheppert — Abtastrate wechseln und Kanäle
 * zusammenlegen.
 */
export async function nachWav(blob: Blob): Promise<Blob> {
  const roh = await blob.arrayBuffer()

  // Nur zum Dekodieren. Danach wieder zu -- ein offener AudioContext hält
  // auf manchen Geräten die Tonausgabe belegt.
  const ctx = new AudioContext()
  let puffer: AudioBuffer
  try {
    puffer = await ctx.decodeAudioData(roh)
  } finally {
    void ctx.close()
  }

  const laenge = Math.max(1, Math.ceil(puffer.duration * ABTASTRATE))
  const offline = new OfflineAudioContext(1, laenge, ABTASTRATE)
  const quelle = offline.createBufferSource()
  quelle.buffer = puffer
  quelle.connect(offline.destination)
  quelle.start()

  const fertig = await offline.startRendering()
  return riff(fertig.getChannelData(0))
}

/**
 * Der WAV-Kopf und die Abtastwerte dahinter.
 *
 * 44 Bytes, seit 1991 unverändert: `RIFF`, Größe, `WAVE`, ein `fmt `-Kasten
 * mit Format 1 (unkomprimiertes PCM) und ein `data`-Kasten. Danach die Werte
 * als ganze Zahlen mit Vorzeichen, kleinstwertiges Byte zuerst.
 */
function riff(werte: Float32Array): Blob {
  const kopf = 44
  const daten = werte.length * 2
  const feld = new ArrayBuffer(kopf + daten)
  const sicht = new DataView(feld)

  const text = (versatz: number, wort: string) => {
    for (let i = 0; i < wort.length; i++) sicht.setUint8(versatz + i, wort.charCodeAt(i))
  }

  text(0, 'RIFF')
  sicht.setUint32(4, 36 + daten, true) // alles nach diesen acht Bytes
  text(8, 'WAVE')
  text(12, 'fmt ')
  sicht.setUint32(16, 16, true) // Länge des fmt-Kastens
  sicht.setUint16(20, 1, true) // 1 = PCM, unkomprimiert
  sicht.setUint16(22, 1, true) // ein Kanal
  sicht.setUint32(24, ABTASTRATE, true)
  sicht.setUint32(28, ABTASTRATE * 2, true) // Bytes je Sekunde
  sicht.setUint16(32, 2, true) // Bytes je Abtastwert über alle Kanäle
  sicht.setUint16(34, 16, true) // Bit je Wert
  text(36, 'data')
  sicht.setUint32(40, daten, true)

  for (let i = 0; i < werte.length; i++) {
    // Begrenzen, bevor skaliert wird: der Renderer kann über 1 hinausgehen,
    // und ohne Deckel klappt der Wert dann um -- aus laut würde knacken.
    const wert = Math.max(-1, Math.min(1, werte[i] ?? 0))
    sicht.setInt16(kopf + i * 2, wert < 0 ? wert * 0x8000 : wert * 0x7fff, true)
  }

  return new Blob([feld], { type: 'audio/wav' })
}
